// libs
import React, { memo, useEffect, useState } from 'react';

// redux
import { addToCartPlatform as _addToCartPlatform } from 'shared/store/cart/services';
import { setCartToast as _setCartToast } from 'shared/store/cart/actions';
import { updateAppCartCount as _updateAppCartCount } from 'shared/store/app/actions';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useSelector } from 'react-redux';

// contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';
import { TOASTER_TYPES, useToastContext } from 'shared/components/Toast';
import { useLoaderContext } from 'shared/components/LoaderProvider/context';
import { useDwebCartContext } from 'shared/components/DwebCart/context';

// constants
import { BREAKPOINTS } from 'shared/styles/base';
import { TEXTS } from './constants';
import { TAB_INDEX } from 'shared/constants';

// helpers
import { getMobileCheckoutURI } from 'shared/helpers/utils/checkout';
import { incrementCartCount } from 'shared/components/CartMerge';

// analytics
import { onAddToCartClick } from 'shared/layouts/DetailsPage/analytics';

// styles
import { AddToCartBtn, ViewBagBtn } from './styles';

// defs
import { ICartAddPartial } from 'shared/api/cart/types';

// analytics
import { onCartClick } from 'shared/components/Header/analytics';

// selectors
import { getProductOffers } from 'shared/store/details/selectors';

interface IAddToBagButtonProps {
  isAddedToCart: boolean;
  onClick: VoidFunction;
  addToCartPlatform: typeof _addToCartPlatform;
  setCartToast: typeof _setCartToast;
  updateAppCartCount: typeof _updateAppCartCount;
  selectedSize?: ProductDetail.ISizeOption;
  hideShowSizeSelector?: VoidFunction;
  wishlistEnabled: boolean;
  disabledOverride?: boolean;
  hideToast?: boolean;
  render?: ({
    addToCartForUser,
    inProgress,
  }: {
    addToCartForUser: VoidFunction;
    inProgress: boolean;
  }) => React.ReactNode;
}

const AddToBagButton = ({
  isAddedToCart,
  onClick,
  selectedSize,
  addToCartPlatform,
  setCartToast,
  updateAppCartCount,
  hideShowSizeSelector,
  wishlistEnabled = false,
  disabledOverride = false,
  hideToast = false,
  render = null,
}: IAddToBagButtonProps) => {
  const { deviceWidth } = useResponsiveContext();
  const { showToast } = useToastContext();
  const { showLoader } = useLoaderContext();
  const { showCart } = useDwebCartContext();
  const [inProgress, setInProgress] = useState(false);

  const { enabled } = useRemoteConfig('saleExperience');

  const pdpOffer = useSelector(getProductOffers);
  const { onSale, specialTag: pdpSpecialTags } = pdpOffer;

  const ButtonComponent = isAddedToCart ? ViewBagBtn : AddToCartBtn;
  const isDesktop: boolean = deviceWidth >= BREAKPOINTS.DESKTOP;
  const handleToast = (
    type: TOASTER_TYPES,
    message: string,
    closeTimeout: number = 1500,
    statusCode = ''
  ) => {
    if (isDesktop || type !== TOASTER_TYPES.SUCCESS) {
      hideShowSizeSelector && hideShowSizeSelector();
      showToast({ type, message, hideTime: closeTimeout, statusCode: statusCode });
    } else {
      setCartToast(message);
      setTimeout(() => setCartToast(''), closeTimeout);
    }
  };
  const goToCheckout = () => {
    if (isDesktop) {
      showCart();
      return;
    }
    showLoader({});
    window.location.href = getMobileCheckoutURI();
  };
  const addToCartForUser = () => {
    if (selectedSize === undefined) {
      onClick();
      return;
    }
    if (isAddedToCart) {
      onCartClick('viewBag');
      goToCheckout();
      return;
    }
    try {
      const deviceType = isDesktop ? 'WEBSITE' : 'MSITE';
      setInProgress(true);
      addToCartPlatform({
        productId: selectedSize.id,
        deviceType,
        quantity: 1,
      })
        .then((response: { message: string; cartPartial: ICartAddPartial }) => {
          setInProgress(false);
          if (!hideToast) {
            handleToast(TOASTER_TYPES.SUCCESS, TEXTS.ADDED_TO_CART, 4000);
          }
          updateAppCartCount(response.cartPartial.quantity);
          incrementCartCount();
          onClick();
          onAddToCartClick({
            product_id: selectedSize.parentId,
            sizeName: selectedSize.sizeName,
            child_product_id: selectedSize.id,
            salePrice: selectedSize.discountedPrice,
            productData: selectedSize,
            saleEnabled: enabled,
            cartSpecialTag: pdpSpecialTags,
            cartSale: onSale
          });
        })
        .catch((err) => {
          handleToast(
            TOASTER_TYPES.ERROR,
            err.message || TEXTS.ADD_TO_CART_ERROR,
            4000,
            err?.statusCode
          );
          setInProgress(false);
        });
    } catch (err) {
      handleToast(TOASTER_TYPES.ERROR, TEXTS.ADD_TO_CART_ERROR);
      setInProgress(false);
    }
  };

  useEffect(() => {
    const windowWithAddToCart = window as Window & {
      addToCartForUser?: typeof addToCartForUser;
    };

    windowWithAddToCart.addToCartForUser = addToCartForUser;

    return () => {
      delete windowWithAddToCart.addToCartForUser;
    };
  }, [addToCartForUser]);
  const disabled = !disabledOverride && !selectedSize;

  if (render) {
    return render({ addToCartForUser, inProgress });
  }
  return (
    <ButtonComponent
      onClick={addToCartForUser}
      data-test="add-btn"
      data-at="add-to-bag"
      inProgress={inProgress}
      wishlistEnabled={wishlistEnabled}
      className="addToBagButtonContainer"
      disabled={disabled}
      tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
    >
      {isAddedToCart ? TEXTS.VIEW_BAG : TEXTS.ADD_TO_BAG}
    </ButtonComponent>
  );
};
export default memo(AddToBagButton);
